import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  type Job,
  type JobApplication,
  type JobApplicationStatus,
} from '@cibli/shared';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { emitDataChanged } from '@/lib/dataEvents';
import { getJobById, updateApplicationStatus } from '@/services/job';

const STATUS_BUTTONS: { labelKey: string; value: JobApplicationStatus; activeClass: string }[] = [
  {
    labelKey: 'dashboard.applications.status.viewed',
    value: 'VIEWED',
    activeClass: 'bg-cj-green/12 text-cj-green-nuance border border-cj-green/25 shadow-sm',
  },
  {
    labelKey: 'dashboard.applications.status.interview',
    value: 'INTERVIEW',
    activeClass: 'bg-cj-blue/20 text-cj-green-dark border border-cj-blue/40 shadow-sm',
  },
  {
    labelKey: 'dashboard.applications.status.accepted',
    value: 'ACCEPTED',
    activeClass: 'bg-cj-green/15 text-cj-green-nuance border border-cj-green/30 shadow-sm',
  },
  {
    labelKey: 'dashboard.applications.status.rejected',
    value: 'REJECTED',
    activeClass: 'bg-cj-pink/10 text-cj-pink border border-cj-pink/25',
  },
  {
    labelKey: 'dashboard.applications.status.noContact',
    value: 'NO_CONTACT',
    activeClass: 'bg-cj-yellow/50 text-cj-green-dark border border-cj-yellow/60 shadow-sm',
  },
];

const INACTIVE_CLASS = 'bg-white text-cj-green-dark/50 border border-cj-green-dark/8';
const INACTIVE_REJECTED_CLASS = 'bg-white text-cj-green-dark/30 border border-cj-green-dark/5';

type ApplicationCardProps = {
  application: JobApplication;
};

export function ApplicationCard({ application }: ApplicationCardProps) {
  const { t, i18n } = useTranslation();
  const [currentStatus, setCurrentStatus] = useState<JobApplicationStatus>(application.status);
  const [isUpdating, setIsUpdating] = useState(false);
  const [jobDetailOpen, setJobDetailOpen] = useState(false);
  const [jobDetail, setJobDetail] = useState<Job | null>(null);
  const [isLoadingJob, setIsLoadingJob] = useState(false);
  const [jobClosed, setJobClosed] = useState(false);

  const companyName = application.companyName ?? t('dashboard.applications.defaultCompany');
  const jobTitle = application.jobTitle ?? t('dashboard.applications.defaultJobTitle');
  const initials = companyName.slice(0, 2).toUpperCase();
  const isRejected = currentStatus === 'REJECTED';

  const appliedDate = new Date(application.appliedAt);
  const dateStr = appliedDate.toLocaleDateString(i18n.language, {
    day: 'numeric',
    month: 'short',
  });

  const handleStatusChange = useCallback(
    async (newStatus: JobApplicationStatus) => {
      if (isUpdating) return;
      const targetStatus = newStatus === currentStatus ? 'SENT' : newStatus;
      const prevStatus = currentStatus;
      setCurrentStatus(targetStatus);
      setIsUpdating(true);
      try {
        await updateApplicationStatus(application.id, targetStatus);
        emitDataChanged('applications-changed');
      } catch {
        setCurrentStatus(prevStatus);
        toast.error(t('dashboard.applications.statusUpdateError'));
      } finally {
        setIsUpdating(false);
      }
    },
    [application.id, currentStatus, isUpdating, t],
  );

  const handleCardClick = useCallback(async () => {
    if (isRejected) return;
    setJobDetailOpen(true);
    setIsLoadingJob(true);
    setJobDetail(null);
    setJobClosed(false);
    try {
      const job = await getJobById(application.jobId);
      if (job) {
        setJobDetail(job);
        const isClosed = Boolean(job.status && job.status !== 'PUBLISHED');
        setJobClosed(isClosed);
      } else {
        setJobClosed(true);
      }
    } catch {
      setJobClosed(true);
    } finally {
      setIsLoadingJob(false);
    }
  }, [application.jobId, isRejected]);

  const avatarColors = [
    'bg-gradient-to-br from-cj-green-light to-cj-blue',
    'bg-gradient-to-br from-cj-pink/25 to-cj-aubergine/15',
    'bg-gradient-to-br from-cj-orange/15 to-cj-orange/5',
    'bg-gradient-to-br from-cj-blue/40 to-cj-blue/20',
    'bg-gradient-to-br from-cj-green/20 to-cj-green/10',
  ];
  const avatarColor = avatarColors[initials.charCodeAt(0) % avatarColors.length];

  const jobDescription = DOMPurify.sanitize(jobDetail?.description || '', { ALLOWED_TAGS: [] });

  return (
    <>
      <div
        className={`pro-card p-5 ${isRejected ? 'opacity-50' : 'cursor-pointer hover:border-cj-green/30 transition-colors'}`}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
      >
        <div className="flex items-center gap-3.5">
          <div
            className={`w-12 h-12 ${isRejected ? 'bg-cj-green-dark/5' : avatarColor} rounded-xl flex items-center justify-center flex-shrink-0`}
          >
            <span
              className={`font-display text-base ${isRejected ? 'text-cj-green-dark/40' : 'text-cj-green-dark'}`}
            >
              {initials}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className={`font-semibold text-base truncate ${isRejected ? 'text-cj-green-dark/60 line-through decoration-cj-green-dark/20' : 'text-cj-green-dark'}`}
            >
              {jobTitle}
            </h3>
            <p
              className={`text-xs ${isRejected ? 'text-cj-green-dark/30' : 'text-cj-green-dark/40'}`}
            >
              {companyName} · {t('dashboard.applications.sent', { date: dateStr })}
            </p>
          </div>
        </div>
        <p className="mt-3 text-xs text-cj-green-dark/60 italic">
          {t('dashboard.applications.alreadyApplied')}
        </p>
        {!isRejected && (
          <p className="mt-2 text-xs text-cj-blue font-medium">
            {t('dashboard.applications.clickToView', {
              defaultValue: 'Cliquez pour voir la description',
            })}
          </p>
        )}
        <div className="flex gap-2 flex-wrap mt-3">
          {STATUS_BUTTONS.map(btn => {
            const isActive = currentStatus === btn.value;
            const inactiveClass = isRejected ? INACTIVE_REJECTED_CLASS : INACTIVE_CLASS;
            return (
              <button
                key={btn.value}
                disabled={isUpdating}
                onClick={e => {
                  e.stopPropagation();
                  handleStatusChange(btn.value);
                }}
                className={`text-xs ${isActive ? 'font-bold' : 'font-semibold'} px-3 py-1.5 rounded-lg transition disabled:opacity-50 ${
                  isActive ? btn.activeClass : inactiveClass
                }`}
              >
                {isActive && btn.value !== 'REJECTED' && '✓ '}
                {isActive && btn.value === 'REJECTED' && '✗ '}
                {t(btn.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <AlertDialog open={jobDetailOpen} onOpenChange={setJobDetailOpen}>
        <AlertDialogContent className="w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold">
              {isLoadingJob
                ? t('dashboard.applications.loading', { defaultValue: 'Chargement...' })
                : jobDetail
                  ? jobDetail.title
                  : t('dashboard.applications.closedTitle', {
                      defaultValue: 'Offre non disponible',
                    })}
            </AlertDialogTitle>
            {!isLoadingJob && (
              <AlertDialogDescription className="text-base whitespace-pre-line">
                {jobClosed
                  ? t('dashboard.applications.closedMessage', {
                      defaultValue:
                        "Cette offre ou campagne est maintenant cloturée. Elle n'est plus disponible.",
                    })
                  : jobDescription ||
                    t('dashboard.applications.noDescription', {
                      defaultValue: 'Aucune description disponible.',
                    })}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {isLoadingJob && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          )}
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setJobDetailOpen(false)}>
              {t('dashboard.applications.close', { defaultValue: 'Fermer' })}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
